import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowUpRight, Sparkles, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { streamAssistant, api } from "@/lib/api";
import { LightMarkdown } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Show all quotes over 1 MNOK without a next step",
  "Which opportunities can realistically close this month?",
  "What is missing before we can submit the Bergen tender?",
  "Summarise the NRK Studio Bergen opportunity",
  "Which products have lead times over 6 weeks?",
];

function Typing() {
  return (
    <span className="inline-flex items-center gap-1 px-0.5 py-1" aria-label="Thinking">
      <span className="typing-dot h-1.5 w-1.5 rounded-full bg-primary" />
      <span className="typing-dot h-1.5 w-1.5 rounded-full bg-primary" />
      <span className="typing-dot h-1.5 w-1.5 rounded-full bg-primary" />
    </span>
  );
}

export function AIAssistant({ open, onOpenChange }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      api
        .get("/ai/assistant/history")
        .then((r) => {
          const hist = [];
          (r.data || []).slice(-3).forEach((row) => {
            hist.push({ role: "user", content: row.question });
            hist.push({ role: "assistant", content: row.answer });
          });
          setMessages(hist);
        })
        .catch(() => {});
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setInput("");
    const history = messages.slice(-6);
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "", streaming: true }]);
    setBusy(true);
    try {
      await streamAssistant(question, history, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: (last.content || "") + delta };
          return copy;
        });
      });
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Sorry - I could not answer that right now (${e.message}).`, streaming: false, error: true };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 bg-background p-0 sm:max-w-[540px]" data-testid="ai-assistant-panel">
        <SheetHeader className="border-b border-black/[0.06] bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--ink))] text-white shadow-[0_8px_18px_-10px_hsl(0_0%_0%/0.6)]">
              <Sparkles className="h-4 w-4 text-[hsl(10_80%_70%)]" />
            </span>
            <div className="min-w-0">
              <SheetTitle className="text-[15px] font-semibold leading-tight">Mediability AI</SheetTitle>
              <SheetDescription className="text-[12.5px]">Answers grounded in your live pipeline, customers, quotes and tenders.</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-thin">
          {messages.length === 0 && (
            <div className="fade-up">
              <div className="eyebrow">Good to see you{firstName ? `, ${firstName}` : ""}</div>
              <h3 className="font-display mt-2 text-[24px] font-semibold leading-tight tracking-[-0.03em]">What do you want to know?</h3>
              <p className="mt-1.5 text-[13.5px] text-muted-foreground">Ask about a customer, a quote, a tender — or try one of these.</p>
              <div className="mt-5 space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => ask(s)}
                    data-testid="ai-suggestion-chip"
                    className={cn("group flex w-full items-center justify-between gap-3 rounded-xl border border-black/[0.07] bg-white px-4 py-3 text-left text-[13.5px] shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] hover:border-primary/40 hover:bg-[hsl(10_76%_61%/0.035)] focus-visible:ring-2 focus-visible:ring-ring/40 fade-up", `d-${Math.min(i + 1, 6)}`)}
                  >
                    <span>{s}</span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-primary" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-5">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex gap-3"}>
                {m.role === "user" ? (
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[hsl(var(--ink))] px-4 py-2.5 text-[13.5px] leading-relaxed text-white shadow-[0_8px_20px_-14px_hsl(0_0%_0%/0.7)]" data-testid="ai-user-message">{m.content}</div>
                ) : (
                  <>
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white ring-1 ring-black/[0.07]"><Sparkles className="h-3 w-3 text-primary" /></span>
                    <div className={cn("min-w-0 max-w-[92%] rounded-2xl rounded-tl-md border bg-white px-4 py-3 shadow-[0_1px_2px_hsl(30_12%_18%/0.04)]", m.error ? "border-destructive/30" : "border-black/[0.06]")} data-testid="ai-assistant-message">
                      {m.content ? <LightMarkdown text={m.content} LinkComponent={Link} className="text-[13.5px] leading-6" /> : <Typing />}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
          className="border-t border-black/[0.06] bg-white px-4 pb-4 pt-3"
        >
          <div className="rounded-2xl border border-black/[0.09] bg-white shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] transition-[border-color,box-shadow] focus-within:border-primary/50 focus-within:shadow-[0_0_0_3px_hsl(10_76%_61%/0.14)]">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  ask();
                }
              }}
              rows={2}
              placeholder="Ask about your pipeline, a customer, a tender…"
              className="min-h-[52px] resize-none border-0 bg-transparent px-4 pt-3 shadow-none focus-visible:border-0 focus-visible:ring-0"
              data-testid="ai-assistant-input"
            />
            <div className="flex items-center justify-between px-2.5 pb-2.5">
              <div className="flex items-center gap-1">
                {messages.length > 0 ? (
                  <Button type="button" variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-[12px] text-muted-foreground" onClick={() => setMessages([])} data-testid="ai-assistant-clear-button">
                    <RotateCcw className="h-3.5 w-3.5" /> New conversation
                  </Button>
                ) : (
                  <span className="px-2 text-[11.5px] text-muted-foreground">Enter to send · Shift+Enter for a new line</span>
                )}
              </div>
              <Button type="submit" size="icon-sm" disabled={busy || !input.trim()} className="rounded-lg" data-testid="ai-assistant-send-button" aria-label="Send">
                <ArrowUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
